import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { LoggerService } from './core/services/logger/logger.service';

interface BigIdConsentValue {
  preferences: { value: boolean; services: string[] };
  statistics: { value: boolean; services: string[] };
  marketing: { value: boolean; services: string[] };
  optedIn: boolean;
}

declare global {
  interface Window {
    _detector?: { stopRecording: () => void; startRecording: () => void };
    bigidcmp?: { setConsent: (consent: BigIdConsentValue, notify: boolean, options?: { addAll: boolean }) => void };
  }
}
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { HeaderComponent } from './core/components/header/header.component';
import { FooterComponent } from './core/components/footer/footer.component';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from './core/services/auth-service/auth.service';
import { ForgerockConfigService } from './core/services/forgerock-config/forgerock-config.service';
import { RedirectService } from './core/services/redirect/redirect.service';
import { IdleTimeoutService } from './core/services/idle-timeout/idle-timeout.service';
import { ROUTES } from './app.routes';
import { QualtricsService, GlassboxService } from 'kemper-component-library';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, TranslateModule, HeaderComponent, FooterComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'customer';
  showSessionExpiredBanner = signal(false);
  showKABanner = signal(false);
  private router = inject(Router);
  currentUrl = signal(this.getCurrentUrl());
  isKaRegistration = computed(() => this.currentUrl().includes('/registration/ka'));
  private sessionExpiredSubscription?: Subscription;
  private idleTimeoutSubscription?: Subscription;
  private sessionResetSubscription?: Subscription;
  private forgerockConfig: ForgerockConfigService = inject(ForgerockConfigService);

  private logger = inject(LoggerService);

  private readonly attStatus = new URLSearchParams(window.location.search).get('attStatus');

  constructor(private authService: AuthService, private redirectService: RedirectService, private idleTimeoutService: IdleTimeoutService) {
    // Step 1: Capture query params for ForgeRock config
    this.captureQueryParams();

    // Step 2: Apply BigID consent based on iOS ATT status (must run before cleanupUrl strips params)
    this.scheduleConsent();
    
    // Step 3: Clean URL and store goto param (preserve 'version' and 'u' params if present)
    this.redirectService.cleanupUrl();
    
    // Step 4: Initialize redirect service to load goto URL
    this.redirectService.initialize();
  }

  ngOnInit(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.url);
      }
    });
    this.sessionExpiredSubscription = this.authService.sessionExpired$.subscribe(() => {
      this.showSessionExpiredBanner.set(true);
      this.router.navigate([ROUTES.LOGIN]);
    });
    this.idleTimeoutSubscription = this.idleTimeoutService.sessionExpired$.subscribe(() => {
      this.showSessionExpiredBanner.set(true);
    });
    this.sessionResetSubscription = this.idleTimeoutService.sessionReset$.subscribe(() => {
      this.showSessionExpiredBanner.set(false);
    });
    this.authService.kaUserStatus$.subscribe(isKA => {
      this.showKABanner.set(isKA);
    });
  }

  private getCurrentUrl(): string {
    // Check if we're navigating to a route by inspecting the current location
    const path = window.location.pathname;
    return path || this.router.url;
  }

  ngOnDestroy() {
    this.sessionExpiredSubscription?.unsubscribe();
    this.idleTimeoutSubscription?.unsubscribe();
    this.sessionResetSubscription?.unsubscribe();
  }

  private scheduleConsent(): void {
    if (window.bigidcmp) {
      this.applyConsent();
    } else {
      window.addEventListener('bigidcmp:widget_ready', () => this.applyConsent(), { once: true });
    }
  }

  private applyConsent(): void {
    const attStatus = this.attStatus;
    this.logger.debug(`applyConsent called with attStatus=${attStatus}`);    if (attStatus === null) return; // Android: no ATT concept, leave consent untouched

    if (!window.bigidcmp) {
      this.logger.warn('bigidcmp is not available - consent update skipped. Ensure BigID CMP is configured and initialized.');
      return;
    }

    const granted = attStatus === 'authorized';
    const consentValue: BigIdConsentValue = {
      preferences: { value: granted, services: [] },
      statistics: { value: granted, services: [] },
      marketing: { value: granted, services: [] },
      optedIn: granted
    };

    if (granted) {
      window.bigidcmp.setConsent(consentValue, true, { addAll: true });
      this.logger.info('All bigidcmp consent categories opted in');
    } else {
      window.bigidcmp.setConsent(consentValue, true);
      this.logger.info('All bigidcmp consent categories denied');
    }
  }

  private captureQueryParams(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const client = urlParams.get('client');
    const version = urlParams.get('version');
    
    this.forgerockConfig.setQueryParams({
      ...(client && { client }),
      ...(version && { version })
    });
  }
}

